import styles from './CollapseTab.module.css'

/* Shared collapse/expand tab used by every dismissible panel in the replay
 * UI. Unifies the "handle sticking out of a panel edge" metaphor:
 *
 *   - InfoViewPanel (left-anchored)   → tab on the RIGHT edge
 *   - TelemetryPanel (right-anchored) → tab on the LEFT edge
 *   - PlayerBottomBar (bottom-anchored) → tab on the TOP edge
 *
 * Visually it's the bottom-bar's old `timelineToggle` silhouette (small
 * rectangular pill, flush with the parent panel) + a discreet teal accent.
 * Collapsed state shifts the accent toward green to signal "expand me back".
 */

type Edge = 'top' | 'bottom' | 'left' | 'right'

interface Props {
  /** Which edge of the parent panel the tab sticks out of. */
  edge: Edge
  /** Current collapsed state — drives icon direction + accent hue. */
  collapsed: boolean
  /** Toggle handler. */
  onToggle: () => void
  /** Optional custom title override. */
  title?: string
  /** Optional classname for external positioning tweaks. */
  className?: string
}

// Chevron direction table. When *expanded*, the arrow points in the direction
// the panel would slide to hide; when *collapsed*, it points back to pull it out.
const ICON: Record<Edge, [expanded: string, collapsed: string]> = {
  top:    ['chevron-down',  'chevron-up'],
  bottom: ['chevron-up',    'chevron-down'],
  left:   ['chevron-right', 'chevron-left'],
  right:  ['chevron-left',  'chevron-right'],
}

// Explicit class map avoids dynamic property access against the CSS-modules
// d.ts (which would require a runtime cast).
const EDGE_CLASS: Record<Edge, string> = {
  top:    styles.edge_top,
  bottom: styles.edge_bottom,
  left:   styles.edge_left,
  right:  styles.edge_right,
}

const DEFAULT_TITLE = (collapsed: boolean) => collapsed ? 'Развернуть' : 'Свернуть'

export default function CollapseTab ({ edge, collapsed, onToggle, title, className }: Props) {
  const [expandedIcon, collapsedIcon] = ICON[edge]
  const icon = collapsed ? collapsedIcon : expandedIcon
  const cls = [
    styles.tab,
    EDGE_CLASS[edge],
    collapsed ? styles.collapsed : styles.expanded,
    className,
  ].filter(Boolean).join(' ')
  return (
    <button
      type="button"
      className={cls}
      onClick={(e) => { e.stopPropagation(); onToggle() }}
      title={title ?? DEFAULT_TITLE(collapsed)}
      aria-label={title ?? DEFAULT_TITLE(collapsed)}
      data-collapse-tab
    >
      <span className={`pixelart-icons-font-${icon}`} />
    </button>
  )
}
